"use client";

import React, { useEffect, useState } from "react";
import { Grid, Box, Card, Typography, CircularProgress, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dashboardService from "../../../services/dashboardService";
import config from "../../../config";

// Import Charts
import RevenueChart from "./RevenueChart";
import ConversationChart from "./ConversationChart";
import CustomerIntentChart from "./CustomerIntent";
import PeakChatTimes from "./PeakChatTime";
import ProductPerformance from "./ProductPerformance";
import EngagementGrowth from "./EngagementGrowth";
import UserDistribution from "./UserDistribution";

// Icons
import StoreIcon from "@mui/icons-material/Store";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ChatIcon from "@mui/icons-material/Chat";
import SyncIcon from "@mui/icons-material/Sync";

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardMetrics = async () => {
      try {
        setLoading(true);
        const clientId = config.clientId;

        if (!clientId) {
          throw new Error("Client ID not found in config");
        }

        const response = await dashboardService.getDashboardMetrics(clientId);

        if (response.success && response.data) {
          setDashboardData(response.data);
          setError(null);
        } else {
          setError(response.message || "Failed to fetch dashboard metrics");
        }
      } catch (err) {
        console.error("Error fetching dashboard metrics:", err);
        setError(err.message || "An error occurred while fetching dashboard metrics");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardMetrics();
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {/* 1️⃣ Quick Actions - First Line Small Buttons */}
      <Card sx={{
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        borderRadius: 2,
        mb: 3,
        p: 2,
        border: "1px solid #e0e0e0"
      }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate("/analytics")}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Export Report
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ChatIcon />}
              onClick={() => navigate("/chat-transcripts")}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              View Conversations
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CreditCardIcon />}
              onClick={() => navigate("/store-management")}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              Manage Credits
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={() => navigate("/pos-sync")}
              sx={{ justifyContent: 'flex-start', py: 1 }}
            >
              POS Management
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* 2️⃣ Store Performance Snapshot - KPI Cards */}
      <Card sx={{
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        borderRadius: 2,
        mb: 3,
        p: 2,
        width: '100%',
        border: "1px solid #e0e0e0"
      }}>
        <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 10 }}>
          {/* Conversations */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "7px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: "120px",
                backgroundColor: "#EFF6FF",
                border: "1px solid #DBEAFE",
                "&:hover": { bgcolor: "#E0E7FF", borderColor: "#93C5FD" }
              }}
              onClick={() => navigate("/chat-transcripts")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 1 }}>
                <Box sx={{ color: "primary.main" }}>
                  <i className="material-symbols-outlined" style={{ fontSize: "32px" }}>
                    chat
                  </i>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: "3px",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: "text.secondary"
                  }}
                >
                  Conversations
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    mb: 0.5
                  }}
                >
                  {dashboardData?.kpi_metrics?.conversations_month || 0}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (dashboardData?.kpi_metrics?.conversations_change || 0) >= 0 ? "success.main" : "error.main",
                    fontWeight: 600
                  }}
                >
                  {(dashboardData?.kpi_metrics?.conversations_change || 0) >= 0 ? "↑" : "↓"} {Math.abs(dashboardData?.kpi_metrics?.conversations_change || 0).toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Unique Users */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "7px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: "120px",
                backgroundColor: "#EFF6FF",
                border: "1px solid #DBEAFE",
                "&:hover": { bgcolor: "#E0E7FF", borderColor: "#93C5FD" }
              }}
              onClick={() => navigate("/chat-transcripts")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 1 }}>
                <Box sx={{ color: "primary.main" }}>
                  <i className="material-symbols-outlined" style={{ fontSize: "32px" }}>
                    people
                  </i>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: "3px",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: "text.secondary"
                  }}
                >
                  Unique Users
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    mb: 0.5
                  }}
                >
                  {dashboardData?.kpi_metrics?.unique_users_month || 0}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (dashboardData?.kpi_metrics?.unique_users_change || 0) >= 0 ? "success.main" : "error.main",
                    fontWeight: 600
                  }}
                >
                  {(dashboardData?.kpi_metrics?.unique_users_change || 0) >= 0 ? "↑" : "↓"} {Math.abs(dashboardData?.kpi_metrics?.unique_users_change || 0).toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Product Clicks */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "7px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: "120px",
                backgroundColor: "#EFF6FF",
                border: "1px solid #DBEAFE",
                "&:hover": { bgcolor: "#E0E7FF", borderColor: "#93C5FD" }
              }}
              onClick={() => navigate("/products")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 1 }}>
                <Box sx={{ color: "primary.main" }}>
                  <i className="material-symbols-outlined" style={{ fontSize: "32px" }}>
                    shopping_cart
                  </i>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: "3px",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: "text.secondary"
                  }}
                >
                  Product Clicks
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    mb: 0.5
                  }}
                >
                  {dashboardData?.kpi_metrics?.product_clicks_month || 0}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (dashboardData?.kpi_metrics?.product_clicks_change || 0) >= 0 ? "success.main" : "error.main",
                    fontWeight: 600
                  }}
                >
                  {(dashboardData?.kpi_metrics?.product_clicks_change || 0) >= 0 ? "↑" : "↓"} {Math.abs(dashboardData?.kpi_metrics?.product_clicks_change || 0).toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Revenue Influenced */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "7px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: "120px",
                backgroundColor: "#EFF6FF",
                border: "1px solid #DBEAFE",
                "&:hover": { bgcolor: "#E0E7FF", borderColor: "#93C5FD" }
              }}
              onClick={() => navigate("/orders")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 1 }}>
                <Box sx={{ color: "primary.main" }}>
                  <i className="material-symbols-outlined" style={{ fontSize: "32px" }}>
                    attach_money
                  </i>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: "3px",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: "text.secondary"
                  }}
                >
                  Orders Influenced
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    mb: 0.5
                  }}
                >
                  ${(dashboardData?.kpi_metrics?.orders_influenced_month || 0).toLocaleString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: (dashboardData?.kpi_metrics?.orders_influenced_change || 0) >= 0 ? "success.main" : "error.main",
                    fontWeight: 600
                  }}
                >
                  {(dashboardData?.kpi_metrics?.orders_influenced_change || 0) >= 0 ? "↑" : "↓"} {Math.abs(dashboardData?.kpi_metrics?.orders_influenced_change || 0).toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Bot Health */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: "7px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: "120px",
                backgroundColor: "#D1FAE5",
                border: "1px solid #A7F3D0",
                "&:hover": { bgcolor: "#A7F3D0", borderColor: "#6EE7B7" }
              }}
              onClick={() => navigate("/")}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 1 }}>
                <Box sx={{ color: "success.main" }}>
                  <i className="material-symbols-outlined" style={{ fontSize: "32px" }}>
                    monitoring
                  </i>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    mb: "3px",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: "text.secondary"
                  }}
                >
                  Bot Health
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    mb: 0.5,
                    color: "success.main"
                  }}
                >
                  ✅ Stable
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                  {(dashboardData?.kpi_metrics?.avg_response_time_ms_month || 0).toFixed(2)}s avg. response
                </Typography>
              </Box>
            </Box>
          </Grid>


        </Grid>
      </Card>

      {/* 3️⃣ Customer Intent & Engagement - 2 Column Layout */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{
            height: '100%',
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: 'hidden',
            p: 3  // Added padding
          }}>
            <CustomerIntentChart data={dashboardData?.customer_intent || []} />
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{
            height: '100%',
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: 'hidden',
            p: 3  // Added padding
          }}>
            <PeakChatTimes data={dashboardData?.peak_chat_times || []} />
          </Card>
        </Grid>
      </Grid>

      {/* 4️⃣ Product Impact - 2 Column Layout */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{
            height: '100%',
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: 'hidden',
            p: 3  // Added padding
          }}>
            <ProductPerformance data={dashboardData?.top_products || []} />
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{
            height: '100%',
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: 'hidden',
            p: 3  // Added padding
          }}>
            <RevenueChart data={dashboardData?.revenue_chart || []} />
          </Card>
        </Grid>
      </Grid>

      {/* 5️⃣ Conversations Over Time & User Distribution - 2 Column Layout */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{
            height: '100%',
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: 'hidden',
            p: 3  // Added padding
          }}>
            <EngagementGrowth
              conversationData={dashboardData?.conversation_chart || []}
            />
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{
            height: '100%',
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: 'hidden',
            p: 3  // Added padding
          }}>
            <UserDistribution data={dashboardData?.user_classification || []} />
          </Card>
        </Grid>
      </Grid>

      {/* 6️⃣ Monthly Conversation Chart */}
      {/* <ConversationChart data={dashboardData?.conversation_chart || []} /> */}
    </>
  );
};

export default ClientDashboard;